// Small-thumbnail helper: rewrites known image hosts to request a resized
// version instead of the full-size original, which makes product thumbnails
// load much faster.
//
// - Unsplash: supports on-the-fly resizing via query params.
// - WordPress/WooCommerce uploads: WP generates resized copies alongside the
//   original with a `-WxH` suffix (e.g. photo-300x300.jpg). If a particular
//   size doesn't exist, the <Thumb> component falls back to the original.

const WP_SIZE = "300x300";

export function thumbUrl(url) {
  if (!url) return url;
  try {
    const u = new URL(url);
    if (u.hostname.includes("images.unsplash.com")) {
      u.searchParams.set("w", "200");
      u.searchParams.set("q", "60");
      u.searchParams.set("auto", "format");
      return u.toString();
    }
    if (u.pathname.includes("/wp-content/uploads/")) {
      // Use (or normalize to) the -300x300 derivative. WooCommerce generates
      // this size (woocommerce_thumbnail) by default; if a particular file
      // doesn't exist, <Thumb> falls back to the original exactly once.
      const m = u.pathname.match(/^(.*?)(-\d+x\d+)?\.(jpe?g|png|webp|gif)$/i);
      if (m) {
        u.pathname = `${m[1]}-${WP_SIZE}.${m[3]}`;
        return u.toString();
      }
    }
    return url;
  } catch {
    return url;
  }
}

// Drop-in <img> replacement: requests the small thumbnail, lazily loads it,
// and falls back to the original URL if the resized file 404s.
export function Thumb({ src, alt = "", className = "", ...rest }) {
  const small = thumbUrl(src);
  return (
    <img
      src={small}
      alt={alt}
      className={className}
      loading="lazy"
      decoding="async"
      onError={(e) => {
        const el = e.currentTarget;
        // Fall back to the original URL at most once, then stop handling
        // errors entirely so a broken original can't retrigger a loop.
        if (small !== src && !el.dataset.fellBack) {
          el.dataset.fellBack = "1";
          el.onerror = null;
          el.src = src;
        }
      }}
      {...rest}
    />
  );
}
